import Vue, { VNode } from "vue";
import { Component, Prop } from "vue-property-decorator";
import parseISO from "date-fns/parseISO";

import { IProductProperty } from "@/services/interfaces";
import Checkbox from "@/components/inputs/Checkbox.vue";

import { ActionQuantity, ActionLogged, ActionGuest } from "./cells";
import * as Buttons from "./buttons";
import { IRow } from "../../interfaces";
import Counter from "@/components/pages/ProductPage/blocks/Counter/Counter.vue";
import { calculateTotal, isDiscounted } from "@/store/cart/utils";
import { pricesModule } from "@/plugins/store-accessors";

@Component({
  name: "TableRowList",

  components: {
    "action-quantity": ActionQuantity,
    "action-logged": ActionLogged,
    "action-guest": ActionGuest,
    "cart-table-button": Buttons.CartTable,
    "catalog-table-button": Buttons.CatalogTable,
    counter: Counter,
    Checkbox,
  },
})
class TableRowList extends Vue {
  @Prop({ type: Object, default: () => ({}) })
  readonly row!: IRow;

  @Prop({ type: String, default: "catalog" })
  readonly context!: string;

  @Prop({ type: Boolean, default: false })
  readonly isAuthorized!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly isInCart!: boolean;

  @Prop({ type: Number, default: 1 })
  readonly quantity!: number;

  @Prop({ type: Function, default: () => null })
  readonly changeQuantity!: () => void;

  @Prop({ type: Function, default: () => null })
  readonly addToCart!: () => void;

  @Prop({ type: Function, default: () => null })
  readonly removeFromCart!: () => void;

  @Prop({ type: Function, default: () => null })
  readonly orderHandler!: () => void;

  @Prop({ type: Function, default: () => null })
  readonly showLoginModal!: () => void;

  @Prop({ type: Function, default: () => null })
  readonly selectedValues!: ({ id, value }: any) => void;

  @Prop({ type: Array, default: () => [] })
  readonly tooltipBreakpoints!: Array<{ value: number; message: string }>;

  @Prop({ type: Boolean, default: false })
  readonly isSelected!: boolean;

  get isOnDiscount() {
    return isDiscounted(this.row);
  }

  get startDiscountTimer() {
    if (this.isOnDiscount) {
      try {
        const dateStartParse = parseISO(this.row.promo_start || "");
        const dateEndParse = parseISO(this.row.promo_end || "");
        const nowDate = new Date().getTime();
        const startDate = dateStartParse?.getTime();
        const endDate = dateEndParse?.getTime();

        return startDate < nowDate && endDate > nowDate && this.row.promo_price;
      } catch (error) {
        return false;
      }
    }
  }

  get hidePrice() {
    return pricesModule.getHidePrice;
  }

  get startDiscountStock() {
    return this.row.promo_stock && this.row.promo_price;
  }

  renderProperty(property: IProductProperty, index: number): VNode {
    return (
      <div key={index} class="product-card__property">
        <span class="product-card__property__title">{property.label}</span>
        <span class="product-card__property__value">
          {property.value || "—"}
        </span>
      </div>
    );
  }

  renderProperties(): VNode | null {
    const { properties } = this.row;

    if (!properties) {
      return null;
    }

    const propertiesSort = [...properties].sort(
      (a, b) => (a.sort || 500) - (b.sort || 500),
    );

    return (
      <div class="product-card__properties">
        <div
          class={`flex-wrapper ${
            properties.length === 4 ? "flex-wrapper--four" : ""
          }`}
        >
          {propertiesSort.map(this.renderProperty)}
        </div>
      </div>
    );
  }

  renderTags() {
    const { onSale, is_top } = this.row;
    const onDiscount = isDiscounted(this.row);

    return (
      <div class="data-name__tags">
        {onSale ? (
          <div class="data-name__tag data-name__tag--list">Распродажа</div>
        ) : null}
        {onDiscount ? (
          <div class="data-name__tag data-name__tag--list data-name__tag--discount">
            Акция
          </div>
        ) : null}
        {is_top ? (
          <div class="data-name__tag data-name__tag--list data-name__tag--top">
            ТОП
          </div>
        ) : null}
      </div>
    );
  }

  renderActions(): VNode {
    const maxCount = this.row.onSale ? this.row.stock : null;
    if (this.context !== "catalog") {
      return (
        <action-quantity
          type="list"
          max={maxCount}
          quantity={this.quantity}
          isOnSale={!!this.row.onSale}
          tooltipBreakpoints={this.tooltipBreakpoints}
          changeHandler={this.changeQuantity}
        >
          <cart-table-button
            slot="buttons"
            title="Удалить"
            type="list"
            action={this.removeFromCart}
          />
        </action-quantity>
      );
    }

    if (this.isInCart) {
      return (
        <btn
          color="transparent"
          style={"width: 100%;"}
          border
          onClick={() => this.$router.push("/cart")}
        >
          <span>В корзине!</span>
        </btn>
      );
    }

    if (this.row.price) {
      return (
        <action-quantity
          type="list"
          max={maxCount}
          quantity={this.quantity}
          isOnSale={!!this.row.onSale}
          tooltipBreakpoints={this.tooltipBreakpoints}
          changeHandler={this.changeQuantity}
        >
          <action-logged
            slot="under"
            title="Быстрый заказ"
            action={this.orderHandler}
          />
          <catalog-table-button
            slot="buttons"
            title="В корзину"
            type="list"
            icon="cart-add"
            color="main"
            action={this.addToCart}
          />
        </action-quantity>
      );
    }

    return <div></div>;
  }

  renderCartPrice(): VNode | null {
    let { price, total_price, promo_price, quantity, promo_stock, stock } =
      this.row;

    const totalPrices = calculateTotal(this.row);
    const promoStockOverflowed = totalPrices.totals.length > 1;
    const sum = totalPrices.totals.reduce((a, b) => a + b);

    return (
      <div class="product-card__price">
        {!this.hidePrice && (
          <div class="product-card__price__value">
            <div class="product-card__price__value--main">
              {sum.toLocaleString("ru")} ₽
            </div>
          </div>
        )}
        {promoStockOverflowed ? (
          <div>
            <div class="product-card__price__block">
              <div class="product-card__price__block--label">
                Товары по акции: <span>{promo_stock} ед.</span>
              </div>
              {!this.hidePrice && (
                <div class="product-card__price__block__value">
                  <span class="product-card__price__block__value--main">
                    {totalPrices.totals[1].toLocaleString("ru")} ₽
                  </span>
                  <span class="promo">
                    {promo_price?.toLocaleString("ru")} / ед.
                  </span>
                </div>
              )}
            </div>

            <div class="product-card__price__block">
              <div class="product-card__price__block--label">
                Товары без акции: <span>{totalPrices.left} ед.</span>
              </div>
              {!this.hidePrice && (
                <div class="product-card__price__block__value">
                  <span class="product-card__price__block__value--main">
                    {totalPrices.totals[0].toLocaleString("ru")} ₽
                  </span>
                  <span>{price?.toLocaleString("ru")} / ед.</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div class="product-card__price__block">
            <div class="product-card__price__block--label">
              Товары по акции: <span>{quantity} ед.</span>
            </div>
            {!this.hidePrice && (
              <div class="product-card__price__block__value">
                <span class="product-card__price__block__value--main">
                  {totalPrices.totals[0].toLocaleString("ru")} ₽
                </span>
                <span class="promo">
                  {promo_price?.toLocaleString("ru")} / ед.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  renderPrice(): VNode | null {
    let { price, total_price, custom_total_price, promo_price, quantity, promo_stock, stock } =
      this.row;

    if (!price) {
      return null;
    }

    if (typeof price === "string") price = parseFloat(price); // float на случай, если если введут копейки
    if (typeof promo_price === "string") promo_price = parseFloat(promo_price);
    if (typeof total_price === "string") total_price = parseFloat(total_price);

    if (this.hidePrice) {
      return null;
    }

    if (!total_price) {
      return (
        <div class="product-card__price__value">
          {promo_price && (
            <div class="product-card__price__value--main">
              {promo_price.toLocaleString()} ₽
            </div>
          )}
          <div class="product-card__price__value--main">
            {price.toLocaleString()} ₽
          </div>
        </div>
      );
    }

    return (
      <div class="product-card__price__value">
        <div class="product-card__price__value--main">
          {custom_total_price?.toLocaleString()} ₽
        </div>
        <div class="product-card__price__value--secondary">
          {price.toLocaleString()} ₽ / ед.
        </div>
      </div>
    );
  }

  renderPriceAndActions() {
    if (this.isAuthorized) {
      let shouldRenderModifiedBlock = false;
      if (this.isInCart && isDiscounted(this.row)) {
        shouldRenderModifiedBlock = true;
      } else if (this.$route.name?.includes("cart") && isDiscounted(this.row)) {
        shouldRenderModifiedBlock = true;
      }

      return [
        shouldRenderModifiedBlock ? this.renderCartPrice() : this.renderPrice(),
        this.renderStock(),
        this.renderActions(),
      ];
    }

    return <action-guest action={this.showLoginModal} />;
  }

  renderCounter() {
    if (this.startDiscountTimer) {
      return (
        <div class="product-card__counter">
          <counter promo_end={this.row.promo_end} />
        </div>
      );
    }

    return null;
  }

  renderStock() {
    const { stock, promo_stock, promo_price } = this.row;
    if (stock && this.isAuthorized) {
      return (
        <div class="product-card__stock-wrap">
          {promo_stock && promo_price && (
            <div class="product-card__stock product-card__stock--discount">
              Доступно по акции: {promo_stock} ед.
            </div>
          )}
          <div class="product-card__stock">В наличии: {stock} ед.</div>
        </div>
      );
    }

    if (!stock && this.isAuthorized && promo_stock && promo_price) {
      return (
        <div class="product-card__stock-wrap">
          {promo_stock && promo_price && (
            <div class="product-card__stock product-card__stock--discount">
              Доступно по акции: {promo_stock} ед.
            </div>
          )}
        </div>
      );
    }

    return null;
  }

  render() {
    const { id, name, image, sku } = this.row;

    return (
      <td colspan="99" class={["product-card_wrapper"]}>
        <div class="product-card-container">
          {this.context !== "catalog" && (
            <div class="table-checkbox-wrap">
              <checkbox
                value={this.isSelected}
                returnValue={(value: boolean) =>
                  this.selectedValues({ id: this.row.id, value })
                }
                className="table-checkbox"
              />
            </div>
          )}
          <div class="product-card">
            <div class="product-card__image">
              <img
                src={
                  image
                    ? "/resize_images/170x170/80/fill" + image.url
                    : "/uploads/default.png"
                }
                alt={image ? image.title : name}
              />
              {this.renderCounter()}
            </div>
            <div class="product-card__info">
              <router-link to={`/product/${id}`}>
                <div class="product-card__title">{name}</div>
              </router-link>
              {sku && (
                <div class="product-card__sku">
                  <span>Артикул: {sku}</span>
                </div>
              )}
              {this.renderProperties()}
              {this.renderTags()}
            </div>
            <div class="product-card__price">
              {this.renderPriceAndActions()}
            </div>
          </div>
        </div>
      </td>
    );
  }
}

export default TableRowList;
